/**
 * Shades a color, `amt` is a percentage.
 *
 * `amt`: Negative values make the color lighter, while positive values make the color darker.
 */

const regexHexSingleDigit = /^#?([\da-fA-F])([\da-fA-F])([\da-fA-F])$/;
const regexHexDoubleDigit = /^#?([\da-fA-F]{2})([\da-fA-F]{2})([\da-fA-F]{2})$/;
const regexRgb = /^rgb\(\d+,\s?\d+,\s?\d+\)$/;
const regexRgba = /^rgba\(\d+,\s?\d+,\s?\d+,\s?(1|0?\.\d+)\)$/;

const clamp0to255 = (x: number) => Math.min(255, Math.max(0, x));

export const shadeColor = (color: string, amt: number) => {
  const hex = rgbToHex(color);
  const num = parseInt(hex.slice(1), 16);
  const R = (num >> 16) + amt; // eslint-disable-line no-bitwise
  const G = ((num >> 8) & 0x00ff) + amt; // eslint-disable-line no-bitwise
  const B = (num & 0x0000ff) + amt; // eslint-disable-line no-bitwise
  const nR = clamp0to255(R) * 0x10000;
  const nG = clamp0to255(G) * 0x100;
  const nB = clamp0to255(B);
  return `#${(0x1000000 + nR + nG + nB) // eslint-disable-line no-nested-ternary
    .toString(16)
    .slice(1)}`;
};

export const hexToRgb = (() => {
  const memo = {};
  return (hex: string) => {
    if (memo[hex]) {
      return memo[hex];
    }
    let res = { r: 0, g: 0, b: 0 };
    const matches =
      regexHexSingleDigit.exec(hex) || regexHexDoubleDigit.exec(hex);
    if (!matches || matches.length !== 4) {
      memo[hex] = res;
      return res;
    }
    const r = parseInt(matches[1], 16);
    const g = parseInt(matches[2], 16);
    const b = parseInt(matches[3], 16);
    res = { r, g, b };
    memo[hex] = res;
    return res;
  };
})();

export const rgbToHex = (() => {
  const memo = {};
  return (rgb: string) => {
    if (memo[rgb]) {
      return `#${memo[rgb]}`;
    }
    const res = extractRgba(rgb)
      .map(v => v.toString(16))
      .map(v => (v.length === 1 ? `0${v}` : v))
      .slice(0, -1)
      .join('');
    memo[rgb] = res;
    return `#${res}`;
  };
})();

export const hexToRgbaString = (hex: string, alpha: number = 1) => {
  if (hex === 'transparent') {
    return hex;
  }

  const r = hexToRgb(hex);
  return `rgba(${r.r}, ${r.g}, ${r.b}, ${alpha})`;
};

export const extractRgba = (colorStr: string): number[] => {
  if (colorStr === 'transparent') {
    return [0, 0, 0, 0];
  }

  if (
    regexHexSingleDigit.test(colorStr) ||
    regexHexDoubleDigit.test(colorStr)
  ) {
    const rgb = hexToRgb(colorStr);
    return [rgb.r, rgb.g, rgb.b, 1];
  }

  if (regexRgb.test(colorStr) || regexRgba.test(colorStr)) {
    const rgb = colorStr
      .match(/[\d.]+/g)!
      .map(v => (/^\d+\.\d+$/.test(v) ? parseFloat(v) : parseInt(v, 10)));
    return rgb.length === 3 ? rgb.concat([1]) : rgb;
  }

  return [0, 0, 0, 0];
};

export const blendColors = (
  color1: string,
  color2: string,
  amount: number,
  alpha: number = 1.0
) => {
  const rgb1 = extractRgba(color1);
  const rgb2 = extractRgba(color2);
  const newRgb = [
    (1 - amount) * rgb1[0] + amount * rgb2[0],
    (1 - amount) * rgb1[1] + amount * rgb2[1],
    (1 - amount) * rgb1[2] + amount * rgb2[2],
  ];
  return `rgba(${newRgb.join(',')},${alpha})`;
};

export const blendHexColors = blendColors;

export const setRgbaAlpha = (color: string, newAlpha: string | number) => {
  const rgba = extractRgba(color);
  return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${newAlpha})`;
};

type RawRgbaColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export const rgbaFromRaw = (
  rgbaColor: RawRgbaColor,
  overrides: Partial<RawRgbaColor> = {}
) => {
  const newColor = { ...rgbaColor, ...overrides };
  return `rgba(${newColor.r}, ${newColor.g}, ${newColor.b}, ${newColor.a})`;
};
